/**
 * Session strength — Surface Layer (/01)
 *
 * Client-side signals for the Auth Flow v2 router gate:
 *   - `isFragileSession()`: decodes `amr` from the current access token —
 *     a password-only session is "fragile" (zero data access; the BFF
 *     enforces this server-side with 403 `step_up_required`).
 *   - `getSecurityGate()`: cached server-derived setup state (password set +
 *     passkey enrolled) from the relying party, plus the e-mail-link entry
 *     flag (operator policy: magic-link arrivals always run the security
 *     ceremony before the app).
 *
 * These drive ROUTING ONLY. Enforcement lives in the BFF (`amr` gate) and the
 * relying party (session-bound register/password). Fail-open by design: if
 * the state fetch fails, routing proceeds and the server gates still hold.
 */

/* global atob */

import { fetchSecurityState, isEmailLinkEntry, clearEmailLinkEntry, type SecurityState } from '@auth-provider';
import { getAccessToken } from './session-token';

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const pad = (parts[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(pad + '==='.slice((pad.length + 3) % 4))) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** True when the current session authenticated ONLY by password. */
export function isFragileSession(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  const amr = Array.isArray(payload?.amr) ? (payload.amr as unknown[]) : [];
  if (amr.length === 0) {
    // Lane 7 fail-closed flip — INERT until VITE_AMR_FAIL_CLOSED=true at build time. Set it ONLY
    // after the custom_access_token_hook is registered + a fresh JWT is verified to carry amr;
    // before that, an amr-less token is still legitimate and must not be treated as fragile.
    return import.meta.env.VITE_AMR_FAIL_CLOSED === 'true';
  }
  const methods = amr
    .map((entry) =>
      typeof entry === 'string' ? entry : ((entry as { method?: string })?.method ?? '')
    )
    .filter(Boolean);
  return methods.length > 0 && methods.every((m) => m === 'password');
}

export interface SecurityGate {
  setupComplete: boolean;
  rotationPending: boolean;
  state: SecurityState | null;
}

let cachedForToken: string | null = null;
let cachedGate: SecurityGate | null = null;

/**
 * Server-derived security gate, cached per access token. `rotationPending`
 * is true for e-mail-link entries until `markCeremonyComplete()`.
 */
export async function getSecurityGate(): Promise<SecurityGate | null> {
  const token = getAccessToken();
  if (!token) return null;
  if (cachedGate && cachedForToken === token) {
    return { ...cachedGate, rotationPending: isEmailLinkEntry() };
  }
  const state = await fetchSecurityState(token);
  if (!state) {
    // Fail-open (RP unreachable / not yet deployed): never lock the surface
    // on a routing hint — the BFF/RP gates are the enforcement.
    cachedForToken = token;
    cachedGate = { setupComplete: true, rotationPending: false, state: null };
  } else {
    cachedForToken = token;
    cachedGate = { setupComplete: state.setupComplete, rotationPending: false, state };
  }
  return { ...cachedGate, rotationPending: isEmailLinkEntry() };
}

/** Synchronous view of the last fetched gate (wizard mode selection). */
export function peekSecurityGate(): SecurityGate | null {
  if (!cachedGate) return null;
  return { ...cachedGate, rotationPending: isEmailLinkEntry() };
}

/** Call when the security ceremony (password + passkey) finishes. */
export function markCeremonyComplete(): void {
  clearEmailLinkEntry();
  cachedForToken = null;
  cachedGate = null;
}
